/**
 * vaccines.js
 */

import React from "react";
import axios from 'axios';
import { 
  styled,
  useMediaQuery,
  Container, 
  Box, 
  List, 
  ListItem, 
  ListItemText,
  ListItemIcon as MuiListItemIcon,
  ListItemButton,
  Typography,
  Divider,
  Link,
  Tooltip,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom"; 
import { format } from "date-fns";
import { makeLocation } from 'utils/routing';
import useMounted from 'utils/use-mounted';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';
import PageTitle from "components/page-title";
import FeatureBox from "components/feature-box";
import DataGrid from 'components/data-grid';
import IconTitle from "components/icon-title";
import Icon from "components/icon";


const ListItemIcon = styled(MuiListItemIcon)(({ theme }) => ({
  width: 36,
  minWidth: 36,
}));


function Table(props) {
  const { data, columnDefs, renderItemCompact } = props;
  const isCompact = useMediaQuery(theme => theme.breakpoints.down('md'), { noSsr: true });
  
  if (data.length === 0) {
    return "None"
  }

  const getRowId = (row) => {
    return row.vaccines
      ? `${row.dogId}-${row.vaccines.replace(/\W+/g, '-').toLowerCase()}`
      : row.dogId;
  };

  return (
    (isCompact && renderItemCompact)
      ? <List 
        sx={{
          padding: 0,
          backgroundColor: 'sys.surfaceContainer',
        }}
      >
        {data.map((item, idx) =>
          <React.Fragment key={getRowId(item)}>
            {renderItemCompact(item)}
            {idx < (data.length - 1) && <Divider />}
          </React.Fragment>
        )}
      </List>
      : <DataGrid
        rows={data}
        columns={columnDefs}
        getRowId={getRowId}
        autoHeight
        disableRowSelectionOnClick
        disableColumnMenu
        hideFooterSelectedRowCount
        hideFooter={data.length <= 25}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25, page: 0 },
          },
        }}
      />
  );
}


function VetVisitLink(props) {
  const { visitId, component: Component, dogHandle, children, ...rest } = props;

  if (visitId) {
    return (
      <Tooltip
        title={`View K9 ${dogHandle}'s booked vet visit`}
        arrow
      >
        <Component
          component={RouterLink}
          to={makeLocation(
            '/records',
            'vetVisit',
            'view',
            { vetVisitId: visitId }
          )}
          {...rest}
        >
          {children}
        </Component>
      </Tooltip>
    );
  } else {
    return children;
  }
}


function DogDisplay(props) {
  const { handlerId, dogId, dogHandle } = props;

  return (
    <Tooltip
      title={`View K9 ${dogHandle}'s vaccination summary`}
      arrow
    >
      <Link
        component={RouterLink}
        to={`/${handlerId}/dogs/${dogId}/edit/vaccinations`}
      >
        {dogHandle}
      </Link>
    </Tooltip>
  );
}


function VacDueTable(props) {
  const { data } = props;

  const columnDefs = [{
    field: 'handle',
    headerName: 'K9',
    flex: 1,
    valueGetter: (params) => ({ handlerId: params.row.userId, dogId: params.row.dogId, dogHandle: params.row.handle }),
    renderCell: (params) => <DogDisplay {...params.value} />
  }, {
    field: 'name',
    headerName: 'Handler',
    flex: 2,
    valueGetter: (params) => `${params.row.handlerFirstName} ${params.row.handlerLastName}`,
  }, {
    field: 'dateNextVaccination',
    headerName: 'Due Date',
    width: 115,
    valueFormatter: (params) => format(params.value, 'MM/dd/yyyy'),
  }, {
    field: 'vaccines',
    headerName: 'Vaccines',
    flex: 4,
  }, {
    field: 'nextVisitId',
    headerName: 'Booked',
    width: 95,
    valueGetter: (params) => ({ visitId: params.row.nextVisitId, dogHandle: params.row.handle }),
    renderCell: (params) => <VetVisitLink 
        component={Link}
        visitId={params.value.visitId}
        dogHandle={params.value.dogHandle}
      >
        {params.value ? 'Yes' : 'No'}
      </VetVisitLink>,
  }];

  const renderItemCompact = (item) => {
    return <ListItem>
      <VetVisitLink
        component={ListItemButton}
        visitId={item.nextVisitId}
        dogHandle={item.dogHandle}
        sx={{ padding: 0 }}
      >
        <ListItemIcon sx={{ alignSelf: 'flex-start' }}>
          <Icon
            type={item.nextVisitId ? 'booked' : 'not-booked'}
            color={item.nextVisitId ? 'success' : 'warning'}
          />
        </ListItemIcon>
        <ListItemText
          sx={{ margin: 0 }}
          primary={<>K9 <DogDisplay handlerId={item.userId} dogId={item.dogId} dogHandle={item.handle} />: {item.handlerFirstName} {item.handlerLastName}</>}
          secondary={<>
            <Typography variant='subtext-small'>{item.vaccines}</Typography>
            <Typography variant='subtext-small' sx={{ fontStyle: 'italic', fontWeight: 'medium' }}>
              due {format(item.dateNextVaccination, 'MM/dd/yyyy')}
            </Typography>
          </>}
        />
        {item.nextVisitId &&
          <Icon type='chevron-right' />
        }
      </VetVisitLink>
    </ ListItem>;
  };

  return <Table {...{ data, columnDefs, renderItemCompact }} />;
}

function IncompleteVacTable(props) {
  const { data } = props;

  const columnDefs = [{
    field: 'handle',
    headerName: 'K9',
    flex: 1,
  }, {
    field: 'name',
    headerName: 'Handler',
    flex: 2,
    valueGetter: (params) => `${params.row.handlerFirstName} ${params.row.handlerLastName}`,
  }];

  const renderItemCompact = (item) => {
    return <ListItem>
      <ListItemIcon>
        <Icon
          type='dog-profile'
        />
      </ListItemIcon>
      <ListItemText
        sx={{ margin: 0 }}
        primary={`K9 ${item.handle}: ${item.handlerFirstName} ${item.handlerLastName}`}
      />
    </ListItem>;
  };

  return <Table {...{ data, columnDefs, renderItemCompact }} />;
}


export default function Vaccines(props) {
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { setAppBar } = React.useContext(AppBarContext);
  const isMounted = useMounted();
  const [loading, setLoading] = React.useState(false);
  const [vaccinationsDue, setVaccinationsDue] = React.useState();
  const [vaccinationsOverdue, setVaccinationsOverdue] = React.useState();
  const [incompleteVaccinationDogs, setIncompleteVaccinationDogs] = React.useState();

  
  // init page
  React.useEffect(() => {
    document.title = 'PACKTRACK - Vaccines';
    setAppBar('Vaccines', []);
  }, [setAppBar]);


  // init - data request
  React.useEffect(() => {
    (async () => {
      setLoading(true);
      const response = await axios.get('/dogs/supervisor-vac-summary');
      if (response.status === 200 && isMounted.current) {
        setVaccinationsDue(response.data.vaccinationsDue);
        setVaccinationsOverdue(response.data.vaccinationsOverdue);
        setIncompleteVaccinationDogs(response.data.incompleteVaccinationDogs);
      }
      setLoading(false);
    })();
    // isMounted is a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container
      sx={theme => ({
        pt: 4,
        [theme.breakpoints.down('md')]: { pt: 3 },
        [theme.breakpoints.down('sm')]: { p: 2 }
      })}     
    >

      {/* desktop title */}
      {!isMobile &&
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <PageTitle iconName='vaccination' title='Vaccines' />
        </Box>
      }

      <FeatureBox style={{ minHeight: 140 }} loading={loading}>
        {vaccinationsDue &&
          <Box pb={1} pt={1} mb={2}>
            <Box mb={2}>
              <IconTitle
                iconType='clock'
                title='Vaccinations Due Within 30 Days'
              />
            </Box>
            <VacDueTable data={vaccinationsDue} />
          </Box>
        }
        {vaccinationsOverdue &&
          <Box pb={1} pt={1} mb={2}>
            <Box mb={2}>
              <IconTitle
                iconType='clock-alert'
                title='Overdue Vaccinations'
              />
            </Box>
            <VacDueTable data={vaccinationsOverdue} />
          </Box>
        }
        {incompleteVaccinationDogs &&
          <Box pb={1} pt={1}>
            <IconTitle
              fullWidth
              iconType='vaccination-missing'
              title='Incomplete Vaccination Records'
              subtitle={<p>In general, all dogs should receive the four core vaccines (plus rabies, in most cases) every three years. The following dogs have no records confirming that they meet this basic requirement.</p>}
            />
            <Box sx={{ maxWidth: 476 }}>
              <IncompleteVacTable data={incompleteVaccinationDogs} />
            </Box>
          </Box>
        }
      </FeatureBox>
    </Container>
  )
}